// ---------------------------------------------------------------------------
// Picking which numbers solutions to show, after the round.
//
// The solver finds the shortest way to the target, and the shortest way is
// frequently not the way a person would ever have found it — `(75 - 3) * 4 - 16`
// is three steps and reads as an accident. Watching the round, the answer people
// actually recognise is the one that gets to a round number near the target and
// then nudges it: for 272, reach 250 and add 22, or reach 300 and take some off.
// So two are shown where two exist, and the second one is chosen by that rule.
//
// **What counts as intuitive, stated exactly**, because "looks nicer" cannot be
// implemented:
//
//   * A LANDMARK is an intermediate result that is a multiple of 100, then 50,
//     then 25, in that order of preference.
//   * It has to be an operand of the FINAL step. That is what makes it a
//     landmark rather than a number that merely occurred somewhere in the
//     working: "250, then add 22" means the 250 is what the last step adjusts.
//   * It has to be NEAR the target, or 100 on the way to 863 would qualify and
//     nothing about that reads as a landmark. Near is `max(25, 15% of target)`,
//     which admits both 250 and 300 for 272 and admits 100 for 101.
//   * The smaller the final adjustment, the better.
//   * Ties break toward fewer steps, then toward no division.
//
// **Two identical picks are one pick.** When the shortest solution is also the
// most intuitive one — which is common, and is the best possible outcome —
// `intuitive` is null and the caller shows a single answer. Showing the same
// working twice under two different headings is worse than showing it once.
//
// This module ranks; it does not solve. It is pure over a list of solutions and
// has no opinion about where they came from.
//
// **Nothing here may be rendered during play.** The numbers screen never says
// whether a player is right, never shows the distance to the target and never
// hints at a solution. This is the reveal's surface, after the round is judged,
// where a solution is already on screen.
// ---------------------------------------------------------------------------

import { stepsToExpression } from '@countdown/shared';
import type { NumbersSolution, NumbersStep } from '@countdown/shared';

export interface NumbersPicks {
  /** The fewest steps to the target. Always present. */
  shortest: NumbersSolution;
  /**
   * The one that goes through a landmark. Null when no solution does, and null
   * when the one that does is `shortest` — the caller then has one answer to
   * draw, which is the whole of the "show one" rule.
   */
  intuitive: NumbersSolution | null;
}

/** How round a landmark is. Higher is better; 0 is not a landmark at all. */
function landmarkGrade(value: number): number {
  if (value % 100 === 0) return 3;
  if (value % 50 === 0) return 2;
  if (value % 25 === 0) return 1;
  return 0;
}

/**
 * How far a landmark may sit from the target and still be one.
 *
 * Proportional with a floor: 15% of 272 is 40, which admits 250 and 300, and the
 * floor keeps small targets from having a window of a few units that nothing can
 * fit through.
 */
function landmarkWindow(target: number): number {
  return Math.max(25, Math.abs(target) * 0.15);
}

function divisionCount(steps: readonly NumbersStep[]): number {
  return steps.filter((step) => step.op === '/').length;
}

/** The value an operand resolves to, for the one step it belongs to. */
function operandValue(
  operand: NumbersStep['left'],
  tiles: readonly number[],
  results: ReadonlyMap<string, number>,
): number | null {
  if (operand.kind === 'tile') return tiles[operand.tileIndex] ?? null;
  return results.get(operand.stepId) ?? null;
}

interface LandmarkFit {
  grade: number;
  /** How much the final step moves the landmark by. Smaller is better. */
  adjustment: number;
}

/**
 * The best landmark this solution passes through, or null.
 *
 * Only the two operands of the last step are considered, and only when they are
 * themselves the result of an earlier step: a raw tile that happens to be 25 is
 * a tile, not a landmark somebody worked their way to.
 */
function landmarkFit(
  solution: NumbersSolution,
  tiles: readonly number[],
  target: number,
): LandmarkFit | null {
  const steps = solution.steps;
  const last = steps[steps.length - 1];
  if (last === undefined) return null;

  const results = new Map<string, number>();
  for (const step of steps) results.set(step.id, step.result);

  const window = landmarkWindow(target);
  let best: LandmarkFit | null = null;

  for (const operand of [last.left, last.right]) {
    if (operand.kind !== 'step') continue;
    const value = operandValue(operand, tiles, results);
    if (value === null) continue;
    const grade = landmarkGrade(value);
    if (grade === 0) continue;
    const adjustment = Math.abs(target - value);
    // A landmark that is already the target is not a landmark plus an
    // adjustment, it is the answer with a step bolted on.
    if (adjustment === 0 || adjustment > window) continue;
    if (best === null || grade > best.grade || (grade === best.grade && adjustment < best.adjustment)) {
      best = { grade, adjustment };
    }
  }

  return best;
}

/**
 * Order two solutions by the tie-breaks that apply everywhere here: fewer steps
 * first, then fewer divisions. Negative when `a` wins.
 */
function byPlainness(a: NumbersSolution, b: NumbersSolution): number {
  if (a.steps.length !== b.steps.length) return a.steps.length - b.steps.length;
  return divisionCount(a.steps) - divisionCount(b.steps);
}

/**
 * The one or two solutions worth showing.
 *
 * `solutions` is what `solveNumbers` returns in its `solutions` field: distinct
 * exact solutions. Ask it for several — one is not a choice. Returns null when
 * there is nothing exact to show, which is the round nobody could solve and
 * where the caller already has the solver's nearest miss to draw instead.
 */
export function pickNumbersSolutions(
  tiles: readonly number[],
  target: number,
  solutions: readonly NumbersSolution[],
): NumbersPicks | null {
  const exact = solutions.filter((solution) => solution.distance === 0);
  if (exact.length === 0) return null;

  const shortest = [...exact].sort(byPlainness)[0];

  const ranked = exact
    .map((solution) => ({ solution, fit: landmarkFit(solution, tiles, target) }))
    .filter((entry): entry is { solution: NumbersSolution; fit: LandmarkFit } => entry.fit !== null)
    .sort((a, b) => {
      if (a.fit.grade !== b.fit.grade) return b.fit.grade - a.fit.grade;
      if (a.fit.adjustment !== b.fit.adjustment) return a.fit.adjustment - b.fit.adjustment;
      return byPlainness(a.solution, b.solution);
    });

  const intuitive = ranked[0]?.solution ?? null;
  if (intuitive === null) return { shortest, intuitive: null };

  // Identity by expression, not by reference: the solver already dedupes its
  // list with this exact key, so two entries that write the same sum cannot be
  // two different answers.
  const same =
    stepsToExpression([...tiles], intuitive.steps) === stepsToExpression([...tiles], shortest.steps);

  return { shortest, intuitive: same ? null : intuitive };
}
